import React, { useState } from 'react';
import { API_URL } from '../../config';
import { authFetch } from '../../utils';

const ALLOWED_ROLES = [
  "admin",
  "viewer",
  "cannon-manager",
  "cannon-viewer",
  "drone-viewer",
  "drone-manager",
  "risk-viewer",
  "meteo-viewer",
  "camera-viewer",
  "soil-sensor-viewer",
  "simulation-viewer",
  "map-layers-viewer",
  "critical-population-layer",
  "alarm-listener",
  "alarm-tester",
  "alerpat-user",
  "preemergencia-sender",
];

export default function UserForm({ user, onSave, onCancel }) {
  const [username, setUsername] = useState(user?.username || '');
  const [fullname, setFullname] = useState(user?.fullname || '');
  const [password, setPassword] = useState('');
  const [roles, setRoles] = useState(user?.roles || []);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const toggleRole = (role) => {
    setRoles(prev =>
      prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!user && !password) {
      setError('La contraseña es obligatoria al crear un usuario');
      return;
    }

    setSaving(true);
    try {
      let res;
      if (user) {
        const updates = { fullname, roles };
        if (password) updates.password = password;

        res = await authFetch(`${API_URL}auth/${user.username}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(updates)
        });
      } else {
        const trimmedUsername = username.trim().toLowerCase();
        res = await authFetch(`${API_URL}auth/register`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username: trimmedUsername, password, roles, fullname })
        });
      }

      if (!res.ok) {
        const message = await res.text();
        setError(message || 'Error al guardar el usuario');
        return;
      }

      onSave({
        username: user ? user.username : username.trim().toLowerCase(),
        fullname,
        roles
      });
    } catch (err) {
      console.error(err);
      setError('Error al guardar el usuario');
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = {
    padding: '0.75rem',
    fontSize: '1rem',
    borderRadius: '6px',
    border: '1px solid #ccc',
    width: '100%'
  };

  const fieldsetStyle = {
    border: '1px solid #ccc',
    borderRadius: '6px',
    padding: '1rem'
  };

  const checkboxLabel = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.35rem',
    marginBottom: '0.5rem',
    marginRight: '1rem',
    fontSize: '0.9rem'
  };

  const primaryButton = {
    padding: '0.75rem 1.5rem',
    backgroundColor: '#007bff',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer'
  };

  const secondaryButton = {
    padding: '0.75rem 1.5rem',
    backgroundColor: '#6c757d',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer'
  };

  return (
    <form
      onSubmit={handleSubmit}
      style={{
        maxWidth: '600px',
        margin: '0 auto',
        padding: '2rem',
        backgroundColor: '#f9f9f9',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem'
      }}
    >
      <h3 style={{ textAlign: 'center', fontSize: '1.5rem', marginBottom: '1rem' }}>
        {user ? 'Editar' : 'Crear'} Usuario
      </h3>

      {error && (
        <div style={{
          backgroundColor: '#f8d7da',
          color: '#721c24',
          padding: '0.75rem',
          borderRadius: '6px',
          border: '1px solid #f5c6cb',
          textAlign: 'center'
        }}>
          {error}
        </div>
      )}

      <input
        value={username}
        onChange={e => setUsername(e.target.value)}
        placeholder="Usuario"
        required
        disabled={!!user}
        style={{
          ...inputStyle,
          backgroundColor: user ? '#e9ecef' : 'white',
          cursor: user ? 'not-allowed' : 'text'
        }}
      />

      <input
        value={fullname}
        onChange={e => setFullname(e.target.value)}
        placeholder="Nombre completo"
        style={inputStyle}
      />

      <input
        type="password"
        value={password}
        onChange={e => setPassword(e.target.value)}
        placeholder={user ? 'Nueva contraseña (dejar en blanco para no cambiar)' : 'Contraseña'}
        required={!user}
        style={inputStyle}
      />

      <fieldset style={fieldsetStyle}>
        <legend style={{ fontWeight: 'bold' }}>Roles</legend>
        {ALLOWED_ROLES.map(role => (
          <label key={role} style={checkboxLabel}>
            <input
              type="checkbox"
              checked={roles.includes(role)}
              onChange={() => toggleRole(role)}
              disabled={role === 'admin'}
            />
            {role}
          </label>
        ))}
      </fieldset>

      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <button type="submit" disabled={saving} style={primaryButton}>
          {saving ? 'Guardando...' : (user ? 'Actualizar' : 'Crear')}
        </button>
        <button type="button" onClick={onCancel} style={secondaryButton}>Cancelar</button>
      </div>
    </form>
  );
}
